import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '~/lib/utils';

export const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      variant: {
        // PRIMARY: Brand violet - main actions
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',

        // SECONDARY: Muted background - supporting actions
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',

        // ACCENT: Dawn Gold - marketing CTAs (use sparingly)
        accent: 'bg-accent text-accent-foreground shadow hover:bg-accent/90',

        // OUTLINE: Neutral border - tertiary actions
        outline: 'border border-input bg-transparent hover:bg-accent/10 hover:text-accent-foreground',

        // GHOST: Transparent - minimal actions
        ghost: 'bg-transparent text-foreground hover:bg-muted/60',

        // DESTRUCTIVE: Errors and dangerous actions
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-9 w-9',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  icon?: React.ReactNode;
  asChild?: boolean;
}

const Slot = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }
>(({ children, ...props }, ref) => {
  if (React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...props,
      ...children.props,
      ref,
    } as any);
  }
  return children as React.ReactElement;
});
Slot.displayName = 'Slot';

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, fullWidth, loading = false, icon, children, disabled, asChild = false, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            {children}
          </span>
        ) : asChild ? (
          children
        ) : (
          <span className="flex items-center gap-2">
            {icon}
            {children}
          </span>
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';
